import { useEffect, useMemo, useState } from 'react';
import { History, RefreshCw, X } from 'lucide-react';
import {
  UniversalPage,
  UniversalTable,
  type UniversalTableColumn,
  Button,
  Card,
  Input,
  PeriodFilter,
  SearchField,
  StatusBadge,
  TABLE_PRESETS,
  UniversalFilterBar,
  type StatusTone,
  NotificationCard,
} from '@afrilink/ui';
import {
  auditService,
  type AuditLogEntry,
  type AuditActorKind,
  type AuditActorScope,
} from '@/domains/audit/services/auditService';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import { sastRangeToTimestamps, type ListWindow } from '@/shared/lib/listWindow';

/**
 * `people` is FIRST and is the default.
 *
 * Measured 2026-09-13: **94%** of this table is system-generated (10,241 of
 * 10,904 rows) —
 * `domain_link.update` 33%, `fulfilment_exception.run` 15%,
 * `phase10_studio_slo.run` 15%, the two `cron_watchdog*.run` 7% each — against
 * `invoice.state_transition` at 1.1%. Defaulting to "All actors" meant anyone
 * asking "what happened to this customer" opened a page that was 94% system
 * events, inside a window of roughly 1.3 days. Switching the default to
 * `people` widens that to about a month at the same 200-row cap — a 24x gain
 * from a default, not from a feature. System events stay one click away and
 * nothing is hidden; the default now matches the question the page is opened
 * to answer.
 */
const ACTOR_OPTIONS: { value: AuditActorScope; label: string }[] = [
  { value: 'people', label: 'People (staff + customers)' },
  { value: 'all', label: 'All actors — incl. system' },
  { value: 'staff', label: 'Staff' },
  { value: 'customer', label: 'Customer' },
  { value: 'system', label: 'System only' },
];

/** The server-side cap. Surfaced to the user through the AR-6 caption. */
const PAGE_LIMIT = 200;

const ACTOR_TONE: Record<AuditActorKind, StatusTone> = {
  customer: 'info',
  staff: 'beta',
  system: 'draft',
};

export default function AuditLogPage() {
  const [win, setWin] = useState<ListWindow<AuditLogEntry>>({
    rows: [],
    truncated: false,
    limit: PAGE_LIMIT,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const [actorFilter, setActorFilter] = useState<AuditActorScope>('people');
  const [eventFilter, setEventFilter] = useState('');

  const [selected, setSelected] = useState<AuditLogEntry | null>(null);

  /**
   * T2-3. The period is SERVER-SIDE and always was — but it is now a
   * `PeriodFilter` rather than two bare date inputs, and it is the only reason
   * this page reaches anything.
   *
   * Measured 2026-09-13: 4,722 rows land in `audit_log` every 30 days against a
   * cap of 200. Unbounded, this page sees about 1.3 DAYS. The original plan
   * proposed moving these bounds into the browser; that would have left a
   * rolling day-and-a-bit with no way to look further back, and it would have
   * looked like it was working.
   *
   * `eventFilter` moves client-side, which is what kills the keystroke refetch:
   * this effect previously listed it as a dependency, so every character typed
   * issued a PostgREST round trip against the largest table on the platform.
   */
  const table = useTableFilters<AuditLogEntry>({
    rows: win.rows,
    dateMode: 'server',
    dateLabel: 'Occurred',
    searchText: (r) =>
      [
        r.customer_email ?? '',
        r.customer_name ?? '',
        r.event,
        r.entity_kind ?? '',
        r.entity_id ?? '',
      ].join(' '),
    facets: useMemo(
      () => [
        (r: AuditLogEntry) =>
          !eventFilter.trim() ||
          r.event.toLowerCase().includes(eventFilter.trim().toLowerCase()),
      ],
      [eventFilter],
    ),
    truncated: win.truncated,
    limit: win.limit,
  });

  const { start: rangeStart, end: rangeEnd } = table.range;

  function load() {
    setLoading(true);
    setError(null);
    const bounds = sastRangeToTimestamps({ start: rangeStart, end: rangeEnd });
    auditService
      .list({
        actor_kind: actorFilter,
        since: bounds.since,
        until: bounds.until,
        limit: PAGE_LIMIT,
      })
      .then((data) => {
        setWin(data);
        setLoading(false);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      });
  }

  useEffect(() => {
    load();
    // `eventFilter` is deliberately ABSENT — it is a client-side substring match
    // now. Its presence here was the keystroke-refetch bug.
  }, [actorFilter, rangeStart, rangeEnd]);

  const filteredRows = table.rows;

  const columns = useMemo<UniversalTableColumn<AuditLogEntry>[]>(
    () => [
      {
        key: 'time',
        header: 'Time',
        sortValue: (r) => new Date(r.created_at),
        render: (r) => (
          <span className="whitespace-nowrap text-muted-foreground tabular-nums">
            {new Date(r.created_at).toLocaleString('en-ZA')}
          </span>
        ),
        width: 'w-44',
      },
      {
        key: 'event',
        header: 'Event',
        sortValue: (r) => r.event,
        render: (r) => <span className="text-xs">{r.event}</span>,
      },
      {
        key: 'actor',
        header: 'Actor',
        sortValue: (r) => r.actor_kind,
        render: (r) => (
          <StatusBadge tone={ACTOR_TONE[r.actor_kind]}>
            {r.actor_kind}
          </StatusBadge>
        ),
      },
      {
        key: 'customer',
        header: 'Customer',
        sortValue: (r) => r.customer_name ?? '',
        render: (r) =>
          r.customer_id ? (
            <div className="flex flex-col">
              <span>{r.customer_name ?? '—'}</span>
              {r.customer_email && (
                <span className="text-xs text-muted-foreground">
                  {r.customer_email}
                </span>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
        hideOnMobile: true,
      },
      {
        key: 'entity',
        header: 'Entity',
        render: (r) =>
          r.entity_kind ? (
            <span className="text-muted-foreground">
              {r.entity_kind}
              {r.entity_id ? (
                <code className="ml-1 text-xs">{r.entity_id}</code>
              ) : null}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
        hideOnMobile: true,
      },
    ],
    []
  );

  return (
    <UniversalPage
      title="Audit log"
      description="Append-only record of every customer-, staff-, and system-driven event."
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Ops' },
        { label: 'Audit' },
      ]}
      actions={
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
          Refresh
        </Button>
      }
    >
      <Card interactive={false} className="p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <label
              htmlFor="actor"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              Actor
            </label>
            <select
              id="actor"
              value={actorFilter}
              onChange={(e) =>
                setActorFilter(e.target.value as AuditActorKind | 'all')
              }
              className="h-9 w-full rounded border border-input bg-card px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {ACTOR_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="event"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              Event
            </label>
            <Input
              id="event"
              type="text"
              placeholder="e.g. whm.suspend_requested"
              value={eventFilter}
              onChange={(e) => setEventFilter(e.target.value)}
              className="h-9"
            />
          </div>
          <div className="sm:col-span-2">
            <label
              htmlFor="audit-search"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              Search
            </label>
            <SearchField
              id="audit-search"
              value={table.q}
              onValueChange={table.setQ}
              pending={table.isSearching}
              placeholder="email, name, entity"
            />
          </div>
        </div>

        {/*
          The period is the load-bearing control on this page, so it gets its own
          row rather than being a fifth item in a grid of five. It is applied
          SERVER-SIDE: at 4,722 rows per 30 days against a cap of 200, an
          unbounded view of this table reaches about 1.3 days.
        */}
        <div className="mt-3 border-t border-border pt-3">
          <UniversalFilterBar
            bordered={false}
            period={
              <PeriodFilter
                value={table.filters.period}
                from={table.filters.from}
                to={table.filters.to}
                onChange={table.filters.setPeriod}
                onStep={table.stepPeriodBy}
                presets={TABLE_PRESETS}
                variant="menu"
                steppable
                label="Occurred"
              />
            }
            activeCount={table.filters.activeCount + (table.q ? 1 : 0)}
            onClear={() => {
              // D-1 (QA, 2026-09-13): Clear must clear the SEARCH too. It reset
              // only the period, so a user pressed Clear and the table stayed
              // filtered by a term they had just been told was gone — and the
              // bar could read "1 filter" while two were applied.
              table.filters.clear();
              table.setQ('');
            }}
          />
        </div>
      </Card>

      {error && (
        <NotificationCard
          tone="danger"
          title="Could not load audit log"
          body={error.message}
        />
      )}

      <UniversalTable<AuditLogEntry>
        data={filteredRows}
        columns={columns}
        getRowKey={(r) => r.id}
        onRowClick={(r) => setSelected(r)}
        isLoading={loading}
        empty={{
          icon: History,
          title: 'No matching events',
          description:
            actorFilter === 'people'
              ? 'Nothing from staff or customers in this window. System events are hidden — switch Actor to "All actors" to include them.'
              : 'Adjust the filters or widen the period.',
        }}
        /*
          AR-6. This page has capped at 200 since the day it shipped and has
          never once said so — and it is 54x past that cap. The caption is the
          most overdue disclosure in the portal: without it, "200 entries" and
          "the 200 most recent of ten thousand" look identical.
        */
        caption={loading ? 'Loading…' : table.caption}
      />

      {selected && (
        <AuditDrawer entry={selected} onClose={() => setSelected(null)} />
      )}
    </UniversalPage>
  );
}

function AuditDrawer({
  entry,
  onClose,
}: {
  entry: AuditLogEntry;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-30 flex" onClick={onClose} role="presentation">
      <div
        className="absolute inset-0 bg-scrim/40 backdrop-blur-sm"
        aria-hidden
      />
      <aside
        className="relative ml-auto flex h-full w-full max-w-2xl flex-col bg-card shadow-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Audit entry detail"
      >
        <header className="flex items-start justify-between gap-3 border-b border-border px-6 py-4">
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">{entry.id}</p>
            <h3 className="mt-0.5 text-sm font-semibold text-foreground">
              {entry.event}
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              {new Date(entry.created_at).toLocaleString('en-ZA')} ·{' '}
              {entry.actor_kind}
              {entry.customer_email ? ` · ${entry.customer_email}` : ''}
              {entry.ip ? ` · ${entry.ip}` : ''}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </header>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <JsonBlock label="Before" value={entry.before} />
            <JsonBlock label="After" value={entry.after} />
          </div>
        </div>
      </aside>
    </div>
  );
}

function JsonBlock({ label, value }: { label: string; value: unknown }) {
  const text = value === null || value === undefined ? '—' : safeStringify(value);
  return (
    <div>
      <p className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <pre className="max-h-[60vh] overflow-auto rounded border border-border bg-muted p-3 text-xs leading-relaxed text-foreground">
        {text}
      </pre>
    </div>
  );
}

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}
